import { Avatar, Badge, Button, Card, EmptyState, Icon, SegmentedControl, Skeleton, StrikeText, Tick, formatDate, useToast } from "@helmr/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { isApiError } from "../../api/client";
import { createTask, deleteTask, doneTask, reopenTask, type TaskItem } from "../../api/tasks";
import { NoAccess } from "../../app/NoAccess";
import { useServices } from "../../app/services";
import { FailureBody } from "../home/states";
import { useStaffNames } from "../leads/hooks";
import { WorkNav } from "./Nav";
import { useScope, useTaskLists, type Scope, type TaskScope } from "./hooks";
import { fu } from "./strings";
import s from "./followups.module.css";

const t = fu.tasks;
export const TITLE_MAX = 140;
/** `GET /tasks` takes one assignee, so "Everyone" is one call per person: this many at most. */
const TEAM_CAP = 25;

export function TasksPage() {
  const sc = useScope();
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const staff = useStaffNames(sc);
  const canTeam = sc.ctx.has("staff.tasks_view_others");
  const canAssign = sc.ctx.has("staff.tasks_assign");
  const [scope, setScope] = useState<TaskScope>("mine");
  const [person, setPerson] = useState("");
  const meId = sc.ctx.me?.staff.id;
  const colleagues = useMemo(() => staff.list.filter((p) => p.id !== meId).map((p) => p.id), [staff.list, meId]);
  const assignees = person ? [person] : colleagues.slice(0, TEAM_CAP);
  const lists = useTaskLists(sc, scope, assignees);
  // "Everyone" is built from the staff list, so until that loads there is nothing to ask for; that is not "no tasks".
  const everyone = scope === "team" && !person;
  const pending = lists.pending || (everyone && staff.pending);
  const error = lists.error ?? (everyone ? staff.error : null);
  const retry = () => { lists.refetch(); if (everyone) void staff.refetch(); };
  const [gone, setGone] = useState<Set<string>>(new Set());
  const [flipped, setFlipped] = useState<Map<string, boolean>>(new Map());

  if (sc.ctx.status !== "loading" && !sc.ctx.has("leads.view") && !sc.ctx.has("staff.tasks_view_others") && sc.ctx.status === "ready" && !sc.ctx.me) return <NoAccess />;

  const tasks = lists.tasks.filter((x) => !gone.has(x.id));
  const doneNow = (x: TaskItem) => flipped.get(x.id) ?? x.doneAt !== null;
  const empty = scope === "mine" ? [t.emptyMine, t.emptyMineBody] : scope === "team" ? [t.emptyTeam, t.emptyTeamBody] : [t.emptyDone, t.emptyDoneBody];
  const open = tasks.filter((x) => !doneNow(x)).length;

  async function toggle(task: TaskItem, next: boolean) {
    setFlipped((m) => new Map(m).set(task.id, next));
    try {
      await (next ? doneTask(client, task.id) : reopenTask(client, task.id));
      await qc.invalidateQueries({ queryKey: ["tasks"] });
    } catch {
      toast.show(t.failed);
    } finally {
      setFlipped((m) => { const n = new Map(m); n.delete(task.id); return n; });
    }
  }
  async function remove(task: TaskItem) {
    try { await deleteTask(client, task.id); setGone((g) => new Set(g).add(task.id)); toast.show(t.removed); void qc.invalidateQueries({ queryKey: ["tasks"] }); }
    catch { toast.show(t.removeFailed); }
  }
  const mayDelete = (x: TaskItem) => canAssign || x.assigneeStaffId === meId || x.createdByStaffId === meId;

  return (
    <div className={s.page}>
      <title>Tasks · Helmr</title>
      <div className={s.head}>
        <div><h1 tabIndex={-1}>{t.title}</h1><p>{pending ? " " : t.sub(open)}</p></div>
        <WorkNav />
      </div>
      <div className={s.filters}>
        <SegmentedControl
          label={t.scopes.label}
          value={scope}
          onChange={setScope}
          options={[
            { value: "mine", label: t.scopes.mine },
            { value: "team", label: t.scopes.team, disabled: !canTeam, title: canTeam ? undefined : t.teamNeeds },
            { value: "done", label: t.scopes.done },
          ]}
        />
        {scope === "team" && canTeam ? (
          <select value={person} onChange={(e) => setPerson(e.target.value)} aria-label="Team member">
            <option value="">Everyone</option>
            {staff.list.filter((p) => p.id !== meId).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        ) : null}
      </div>

      {everyone && colleagues.length > TEAM_CAP ? <p className={s.note}>{t.teamCapped(TEAM_CAP)}</p> : null}
      <Card>
        {pending ? (
          <div aria-busy="true" data-testid="tasks-skeleton">{[0, 1, 2, 3].map((i) => <div className={s.skRow} key={i}><Skeleton width={18} height={18} /><div style={{ display: "grid", gap: 6 }}><Skeleton width="60%" height={13} /><Skeleton width="30%" height={12} /></div></div>)}</div>
        ) : error ? <FailureBody error={error} title={t.error} what="Tasks" onRetry={retry} />
        : tasks.length === 0 ? <EmptyState icon="check" title={empty[0]!} description={empty[1]} />
        : (
          <div className={s.tasks}>
            {tasks.map((x) => <TaskRow key={x.id} task={x} done={doneNow(x)} sc={sc} onToggle={toggle} onRemove={mayDelete(x) ? remove : undefined} />)}
            {lists.truncated ? <p className={s.note}>{t.truncated}</p> : null}
          </div>
        )}
        {scope === "mine" ? <QuickAdd sc={sc} canAssign={canAssign && staff.canList} staff={staff.list} /> : null}
      </Card>
    </div>
  );
}

function TaskRow({ task, done, sc, onToggle, onRemove }: { task: TaskItem; done: boolean; sc: Scope; onToggle: (x: TaskItem, next: boolean) => void; onRemove?: (x: TaskItem) => void }) {
  const when = task.dueDate === null ? null : task.dueDate === sc.date ? t.dueToday : t.dueOn(formatDate(task.dueDate, sc.ctx.locale, "weekday"));
  const link = task.link ? (task.link.type === "lead" ? `/leads/${task.link.id}` : `/members/${task.link.id}`) : null;
  return (
    <div className={`${s.task} ${done ? s.doneRow : ""}`} data-testid="task-row">
      <Tick checked={done} onChange={(next) => onToggle(task, next)} label={t.doneLabel(task.title)} />
      <div className={s.taskBody}>
        <StrikeText done={done}><span className={s.taskTitle} title={task.title}>{task.title}</span></StrikeText>
        <span className={s.meta}>
          {task.assigneeName ? <><Avatar id={task.assigneeStaffId} name={task.assigneeName} size="xs" />{task.assigneeName} · </> : null}
          {done ? t.doneWord : (when ?? t.noDue)}{task.dueTime && !done ? ` ${task.dueTime}` : ""}
          {task.overdue && !done ? <> · <Badge tone="crit">{t.overdue}</Badge></> : null}
          {link ? <> · <Link to={link}>{task.link!.type === "lead" ? t.linkLead : t.linkMember}</Link></> : null}
        </span>
      </div>
      {onRemove ? <button className={s.iconBtn} aria-label={t.removeLabel(task.title)} title={t.remove} onClick={() => onRemove(task)}><Icon name="x" /></button> : null}
    </div>
  );
}

function QuickAdd({ sc, canAssign, staff }: { sc: Scope; canAssign: boolean; staff: Array<{ id: string; name: string }> }) {
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [due, setDue] = useState(sc.date);
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState(false);
  async function add(e: React.FormEvent) {
    e.preventDefault();
    const text = title.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      await createTask(client, { title: text.slice(0, TITLE_MAX), dueDate: due || null, ...(sc.branchId ? { branchId: sc.branchId } : {}), ...(canAssign && assignee ? { assigneeStaffId: assignee } : {}) });
      setTitle("");
      setAssignee("");
      await qc.invalidateQueries({ queryKey: ["tasks"] });
    } catch (err) {
      toast.show(isApiError(err) && err.status === 422 ? err.message : t.addFailed);
    } finally { setBusy(false); }
  }
  return (
    <form className={s.qadd} onSubmit={add}>
      <input value={title} maxLength={TITLE_MAX} onChange={(e) => setTitle(e.target.value)} placeholder={t.addPlaceholder} aria-label={t.addLabel} autoComplete="off" />
      <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label={t.due} />
      {canAssign ? (
        <select value={assignee} onChange={(e) => setAssignee(e.target.value)} aria-label={t.assignee}>
          <option value="">{t.assignee}: {t.assignMe}</option>
          {staff.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      ) : null}
      <Button size="sm" type="submit" busy={busy} busyLabel={t.adding}>{t.add}</Button>
    </form>
  );
}
